/* Focal-point helpers for tenant portfolio images.

   A "focal point" tells the browser where the SUBJECT of a photo sits so
   that when the image is cropped to a fixed aspect ratio (portrait tiles,
   hero bands, mobile 1:1 thumbs, etc.) the subject stays in-frame instead
   of getting decapitated by the default `object-position: center`.

   Storage model: `site_settings.portfolio_focal_points` is a parallel array
   to `portfolio_images`. Each entry is `{ x: 0-100, y: 0-100 }` — percent
   from top-left. Missing / null / empty entries default to center (50,50)
   which matches the pre-focal-point behaviour of every template.

   Usage in a template:

     import { getFocalStyle } from "@/lib/focal";
     ...
     <img
       src={settings.portfolio_images[i]}
       style={{ ...getFocalStyle(settings.portfolio_focal_points, i), objectFit: "cover" }}
     />
*/

/** Return an inline `style` fragment that positions the image's focal point.
    Always safe to call — bad indices, missing arrays, malformed entries all
    fall back to center. */
export function getFocalStyle(focalPoints, index) {
  const arr = Array.isArray(focalPoints) ? focalPoints : [];
  const raw = arr[index];
  const x = clamp01(raw?.x, 50);
  const y = clamp01(raw?.y, 50);
  return { objectPosition: `${x}% ${y}%` };
}

/** Same data, but as a plain string suitable for `background-position`. */
export function getFocalBackgroundPosition(focalPoints, index) {
  const arr = Array.isArray(focalPoints) ? focalPoints : [];
  const raw = arr[index];
  const x = clamp01(raw?.x, 50);
  const y = clamp01(raw?.y, 50);
  return `${x}% ${y}%`;
}

function clamp01(n, fallback) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}
